import { buildHeaders } from 'application/services/buildHeaders';
import { ApiError, ApiRedirect } from 'helpers/error';
import axios, { AxiosRequestConfig } from 'axios';
import { CONTENT_API_URL, DOMAIN_PORT, DOMAIN_PROTOCOL } from './constants';

export interface NavigationAdapterRequestInterface {
	host: string | string[];
	accessToken?: string;
	locale: string;
}

export interface NavigationAdapterResponseInterface {
	properties: Record<string, unknown>;
	children: Array<NavigationAdapterResponseInterface>;
	id: number;
	culture: string;
	name: string;
	url: string;
	documentType: string;
	template: string;
	createDate: string;
	sortOrder: number;
}

export const getCmsNavigation = async ({
	host,
	accessToken,
	locale,
}: NavigationAdapterRequestInterface): Promise<Navigation.NestedNavigationItem> => {
	if (!CONTENT_API_URL) {
		throw new ApiError('CONTENT_API_URL:NOTDEFINED', 500, 'Content api url is not defined');
	}

	if (host === 'localhost' && process.env.FALLBACK_HOSTNAME) {
		host = process.env.FALLBACK_HOSTNAME;
	}

	const path = '';
	const method = 'POST';

	// We cannot make recursive expressions in GraphQL so we
	// assume 5 levels is enough for most use cases
	const query = `{
		navigation: navigation
	}`;

	const headers = buildHeaders({ domain: host as string, accessToken, locale });
	try {
		// Function to transform JSON to a more friendly structure
		// and discard all irrelevant data
		const transform = (item): Array<Navigation.NestedNavigationItem> => {
			const transformed = item?.map((child) => {
				const isChildNavLink = child.properties?.navigationLinkUrl;

				let url = child.url;

				if (url?.includes(host)) {
					//TODO: This might just be a hack. Next needs relative urls for this to work correctly but they're returned as full paths. Should probably come from the back end as relative.
					url = url.replace(host as string, '');
				}

				return {
					id: child.id || null,
					name: child.name || null,
					url: (!isChildNavLink ? url : child.properties.navigationLinkUrl.Url) || null,
					target: (child.properties?.navigationLinkUrl?.target ?? '_self') || null,
					linkType: child.properties?.navigationLinkUrl?.linkType ?? '',
					showAsButton: child.properties?.showAsButton ?? false,
					...(child.properties?.pageSettings?.length
						? child.properties?.pageSettings.map((pageSetting) => {
								if (pageSetting.documentType === 'navigation') {
									const { umbracoNaviHide, navigationTitle } = pageSetting?.content?.properties ?? {};
									return {
										hidden: umbracoNaviHide ? true : false,
										title: navigationTitle ? navigationTitle : null,
									};
								}
								return null;
						  })[0]
						: null),
					decendants: (!isChildNavLink && transform(child.children)) || null,
				};
			});
			return transformed;
		};

		const requestConfig: AxiosRequestConfig = {
			url: `${CONTENT_API_URL}${path ? path : ''}`,
			method: method,
			maxRedirects: 0,
			headers,
			data: {
				query,
			},
		};

		const res = await axios(requestConfig);
		if (method === 'POST') {
			const { data, errors } = res.data;

			if (errors?.length) {
				throw new Error(errors[0].message);
			}

			if (!data?.navigation) {
				throw new Error('CMS returned no navigation data');
			}

			const transformedData: Navigation.NestedNavigationItem = {
				...transform([data.navigation])[0],
			};

			return transformedData;
		}

		if (method === 'GET') {
			const { data } = res;
			const transformedData: Navigation.NestedNavigationItem = {
				...transform([data.navigation])[0],
			};

			return transformedData;
		}
	} catch (e) {
		throw new Error(`Get CMS navigation failed. Cause: ${e.message}`, { cause: e });
	}
};
